import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

import en from "./en.json";
import es from "./es.json";
import fr from "./fr.json";
import de from "./de.json";

// Same pattern as ../affiliates/src/i18n: own context, JSON dictionaries, no
// i18next. English is the source of truth; any key missing from a translation
// falls back to English rather than rendering a raw key.
//
// es/fr/de are intentionally empty right now. Translation is a separate step,
// and an empty dictionary renders a fully correct English page.

const translations = { en, es, fr, de };

export const LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "es", label: "Spanish", nativeLabel: "Espanol" },
  { code: "fr", label: "French", nativeLabel: "Francais" },
  { code: "de", label: "German", nativeLabel: "Deutsch" },
];

const SUPPORTED = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANG = "en";
const STORAGE_KEY = "lang";

function detectLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch {
    // Private mode or blocked storage. Fall through to browser detection.
  }
  const browser = (navigator.language || DEFAULT_LANG).split("-")[0].toLowerCase();
  return SUPPORTED.includes(browser) ? browser : DEFAULT_LANG;
}

function get(obj, path) {
  return path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
}

function interpolate(str, vars) {
  if (!vars || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, key) => (vars[key] !== undefined ? vars[key] : `{${key}}`));
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  const dict = useMemo(() => translations[lang] || translations.en, [lang]);

  // Returns a string. Missing keys fall back to English, then to the key
  // itself so a typo is visible in the UI rather than silently blank.
  const t = useCallback(
    (key, vars) => {
      const val = get(dict, key) ?? get(translations.en, key) ?? key;
      return interpolate(val, vars);
    },
    [dict]
  );

  // Returns an array. Used for the bullet blocks, which are arrays in the
  // dictionaries. Always returns an array so callers can map without guarding.
  const tList = useCallback(
    (key, vars) => {
      const val = get(dict, key) ?? get(translations.en, key);
      if (!Array.isArray(val)) return [];
      return vars ? val.map((item) => interpolate(item, vars)) : val;
    },
    [dict]
  );

  // Returns an array of objects, e.g. the legal pages' [{ heading, body }].
  const tBlocks = useCallback(
    (key, vars) => {
      const val = get(dict, key) ?? get(translations.en, key);
      if (!Array.isArray(val)) return [];
      if (!vars) return val;
      return val.map((block) =>
        Object.fromEntries(
          Object.entries(block).map(([k, v]) => [
            k,
            Array.isArray(v) ? v.map((x) => interpolate(x, vars)) : interpolate(v, vars),
          ])
        )
      );
    },
    [dict]
  );

  const setLang = useCallback((code) => {
    if (!SUPPORTED.includes(code)) return;
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Non-fatal: the choice just will not persist across reloads.
    }
    setLangState(code);
    document.documentElement.setAttribute("lang", code);
  }, []);

  const value = useMemo(
    () => ({ t, tList, tBlocks, lang, setLang, LANGUAGES }),
    [t, tList, tBlocks, lang, setLang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used within I18nProvider");
  return ctx;
}
